import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Table } from 'react-bootstrap';
import moment from 'moment';

export const BalanceSheetReport = ({ balanceSheet, endDate, activeProject, accounts, formatCurrency }) => {
  // Determine if this is a comparison report
  const isComparison = balanceSheet && Array.isArray(balanceSheet.comparisons) && balanceSheet.comparisons.length > 0;
  
  // Extract reports
  const baseData = balanceSheet && balanceSheet.baseData ? balanceSheet.baseData : balanceSheet;
  const comparisons = isComparison ? balanceSheet.comparisons : [];

  // Helper to render account category name with links if applicable
  const renderAccountNameWithLink = (name, indentClass = "pl-3", colSpan = 1) => {
    const cleanName = name.replace(/^[\s\u00A0]+/, '').replace(/^\d+\s*·\s*/, '');
    const targetAcc = accounts.find(a => a.name === cleanName);
    
    // Convert bootstrap pl classes to explicit left padding styles for bulletproof rendering
    let paddingLeft = '1rem';
    if (indentClass.includes('pl-1')) paddingLeft = '0.25rem';
    else if (indentClass.includes('pl-2')) paddingLeft = '0.5rem';
    else if (indentClass.includes('pl-3')) paddingLeft = '1rem';
    else if (indentClass.includes('pl-4')) paddingLeft = '1.75rem';
    else if (indentClass.includes('pl-5')) paddingLeft = '2.5rem';
    else if (indentClass.includes('pl-6')) paddingLeft = '3.25rem';

    return (
      <td className={indentClass} colSpan={colSpan} style={{ paddingLeft }}>
        {targetAcc ? (
          <Link to={`/accounts/${targetAcc._id}/transactions?endDate=${endDate}&posting=posted`} className="text-decoration-none text-muted font-weight-bold" style={{ cursor: 'pointer' }}>
            {name}
          </Link>
        ) : (
          name
        )}
      </td>
    );
  };

  // Helper to render cell value
  const renderCellAmount = (name, val, isLink = true) => {
    const cleanName = name.replace(/^[\s\u00A0]+/, '').replace(/^\d+\s*·\s*/, '');
    const targetAcc = accounts.find(a => a.name === cleanName);
    const textVal = formatCurrency(val);
    
    if (isLink && targetAcc) {
      return (
        <Link to={`/accounts/${targetAcc._id}/transactions?endDate=${endDate}&posting=posted`} className="text-decoration-none text-muted font-weight-bold" style={{ cursor: 'pointer' }}>
          {textVal}
        </Link>
      );
    }
    return textVal;
  };

  // Computes the change columns between base value and latest comparison value
  const renderChangeCells = (baseVal, name, detailsKey, customColorClass = null) => {
    if (!isComparison) return null;
    
    // Latest comparison period is the first comparison date in comparisons array
    const latestComp = comparisons[0];
    let compVal = 0;
    if (latestComp) {
      if (name === 'liabilities_equity_total_sum_placeholder') {
        compVal = latestComp.liabilitiesTotal + latestComp.equityTotal;
      } else if (detailsKey && latestComp.balances && latestComp.balances[name] !== undefined) {
        compVal = latestComp.balances[name];
      } else if (!detailsKey) {
        // Otherwise, it might be a total value
        compVal = latestComp[name] || 0;
      }
    }

    const diff = compVal - baseVal;
    const pct = baseVal !== 0 ? (diff / baseVal) * 100 : 0;
    
    // An increase (diff > 0) is positive, a decrease (diff < 0) is negative.
    const isDecrease = diff < -0.005;
    const isIncrease = diff > 0.005;
    const defaultColor = customColorClass || 'text-dark';
    const colorClass = isDecrease ? 'text-danger font-weight-bold' : (isIncrease ? 'text-success font-weight-bold' : defaultColor);
    
    const arrow = isIncrease ? '▲ ' : (isDecrease ? '▼ ' : '');

    return (
      <>
        <td className={`text-right ${colorClass}`}>{formatCurrency(diff)}</td>
        <td className={`text-right ${colorClass}`}>{arrow}{baseVal !== 0 ? `${pct.toFixed(1)}%` : '0.0%'}</td>
      </>
    );
  };

  const renderBalanceSheetSection = (groupedData) => {
    if (!groupedData) return null;
    const { assets, liabilities, equity } = groupedData;
    
    // Dynamic colSpan for structural rows
    // Account name (1) + Base period (1) + N comparisons + Change $ (1) + Change % (1)
    const totalCols = 1 + 1 + comparisons.length + (isComparison ? 2 : 0);

    const renderSubCategoryRows = (catName, catData, detailIndentClass = "pl-5", headerIndentClass = "pl-4", totalKeyName) => {
      if (!catData || !catData.details || Object.keys(catData.details).length === 0) return null;
      return (
        <>
          <tr className="font-weight-bold text-muted small"><td className={`${headerIndentClass} text-uppercase`} colSpan={totalCols}>{catName}</td></tr>
          {Object.entries(catData.details).map(([name, amount]) => {
            return (
              <tr key={name} className="extra-small">
                {renderAccountNameWithLink(name, `${detailIndentClass} text-muted`)}
                <td className="text-right text-dark">{renderCellAmount(name, amount)}</td>
                {comparisons.map((comp, idx) => (
                  <td key={idx} className="text-right text-muted">{formatCurrency(comp.balances[name] || 0)}</td>
                ))}
                {renderChangeCells(amount, name, true)}
              </tr>
            );
          })}
          <tr className="font-weight-bold extra-small text-muted">
            <td className={headerIndentClass}>Total for {catName}</td>
            <td className="text-right border-top">{formatCurrency(catData.total)}</td>
            {comparisons.map((comp, idx) => (
              <td key={idx} className="text-right border-top">{formatCurrency(comp[totalKeyName] || 0)}</td>
            ))}
            {renderChangeCells(catData.total, totalKeyName, false)}
          </tr>
        </>
      );
    };

    return (
      <Table responsive borderless className="report-table mb-0">
        <thead>
          <tr className="border-bottom text-muted font-weight-bold small">
            <th>Account Category</th>
            <th className="text-right">As of {moment(endDate).format('MM/DD/YYYY')} (Base)</th>
            {comparisons.map((comp, idx) => (
              <th key={idx} className="text-right">As of {moment(comp.date).format('MM/DD/YYYY')}</th>
            ))}
            {isComparison && (
              <>
                <th className="text-right">Change vs {moment(endDate).format('MM/DD/YYYY')} ($)</th>
                <th className="text-right">Change vs {moment(endDate).format('MM/DD/YYYY')} (%)</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {/* ASSETS */}
          <tr className="bg-light font-weight-bold"><td colSpan={totalCols} className="text-uppercase text-primary pl-1" style={{ paddingLeft: '0.25rem' }}>ASSETS</td></tr>
          
          {/* Current Assets */}
          <tr className="font-weight-bold small"><td className="pl-3" colSpan={totalCols} style={{ paddingLeft: '1rem' }}>Current Assets</td></tr>
          {renderSubCategoryRows("Bank Accounts", assets.currentAssets.bank, "pl-5", "pl-4", "bankTotal")}
          {renderSubCategoryRows("Accounts Receivable", assets.currentAssets.accountsReceivable, "pl-5", "pl-4", "accountsReceivableTotal")}
          {renderSubCategoryRows("Other Current Assets", assets.currentAssets.otherCurrentAssets, "pl-5", "pl-4", "otherCurrentAssetsTotal")}
          
          <tr className="font-weight-bold small">
            <td className="pl-3" style={{ paddingLeft: '1rem' }}>Total for Current Assets</td>
            <td className="text-right border-top border-dark">{formatCurrency(assets.currentAssets.total)}</td>
            {comparisons.map((comp, idx) => (
              <td key={idx} className="text-right border-top border-dark">{formatCurrency(comp.currentAssetsTotal)}</td>
            ))}
            {renderChangeCells(assets.currentAssets.total, 'currentAssetsTotal', false)}
          </tr>

          {/* Fixed Assets */}
          {assets.fixedAssets && Object.keys(assets.fixedAssets.details).length > 0 && (
            <>
              <tr className="font-weight-bold small mt-2"><td className="pl-3" colSpan={totalCols} style={{ paddingLeft: '1rem' }}>Fixed Assets</td></tr>
              {Object.entries(assets.fixedAssets.details).map(([name, amount]) => (
                <tr key={name} className="extra-small">
                  {renderAccountNameWithLink(name, "pl-4")}
                  <td className="text-right">{renderCellAmount(name, amount)}</td>
                  {comparisons.map((comp, idx) => (
                    <td key={idx} className="text-right text-muted">{formatCurrency(comp.balances[name] || 0)}</td>
                  ))}
                  {renderChangeCells(amount, name, true)}
                </tr>
              ))}
              <tr className="font-weight-bold small">
                <td className="pl-3" style={{ paddingLeft: '1rem' }}>Total for Fixed Assets</td>
                <td className="text-right border-top">{formatCurrency(assets.fixedAssets.total)}</td>
                {comparisons.map((comp, idx) => (
                  <td key={idx} className="text-right border-top">{formatCurrency(comp.fixedAssetsTotal)}</td>
                ))}
                {renderChangeCells(assets.fixedAssets.total, 'fixedAssetsTotal', false)}
              </tr>
            </>
          )}

          {/* Other Assets */}
          {assets.otherAssets && Object.keys(assets.otherAssets.details).length > 0 && (
            <>
              <tr className="font-weight-bold small mt-2"><td className="pl-3" colSpan={totalCols} style={{ paddingLeft: '1rem' }}>Other Assets</td></tr>
              {Object.entries(assets.otherAssets.details).map(([name, amount]) => (
                <tr key={name} className="extra-small">
                  {renderAccountNameWithLink(name, "pl-4")}
                  <td className="text-right">{renderCellAmount(name, amount)}</td>
                  {comparisons.map((comp, idx) => (
                    <td key={idx} className="text-right text-muted">{formatCurrency(comp.balances[name] || 0)}</td>
                  ))}
                  {renderChangeCells(amount, name, true)}
                </tr>
              ))}
              <tr className="font-weight-bold small">
                <td className="pl-3" style={{ paddingLeft: '1rem' }}>Total for Other Assets</td>
                <td className="text-right border-top">{formatCurrency(assets.otherAssets.total)}</td>
                {comparisons.map((comp, idx) => (
                  <td key={idx} className="text-right border-top">{formatCurrency(comp.otherAssetsTotal)}</td>
                ))}
                {renderChangeCells(assets.otherAssets.total, 'otherAssetsTotal', false)}
              </tr>
            </>
          )}

          <tr className="font-weight-bold border-top border-bottom border-dark bg-light text-primary small">
            <td className="pl-1" style={{ paddingLeft: '0.25rem' }}>TOTAL ASSETS</td>
            <td className="text-right">{formatCurrency(assets.total)}</td>
            {comparisons.map((comp, idx) => (
              <td key={idx} className="text-right">{formatCurrency(comp.assetsTotal)}</td>
            ))}
            {renderChangeCells(assets.total, 'assetsTotal', false)}
          </tr>

          {/* LIABILITIES AND EQUITY */}
          <tr className="bg-light font-weight-bold"><td colSpan={totalCols} className="text-uppercase text-primary mt-4 pl-1" style={{ paddingLeft: '0.25rem' }}>LIABILITIES AND EQUITY</td></tr>
          
          {/* Liabilities */}
          <tr className="font-weight-bold small"><td className="pl-3" colSpan={totalCols} style={{ paddingLeft: '1rem' }}>Liabilities</td></tr>
          
          {/* Current Liabilities */}
          <tr className="font-weight-bold small"><td className="pl-4 text-muted" colSpan={totalCols} style={{ paddingLeft: '1.75rem' }}>Current Liabilities</td></tr>
          {renderSubCategoryRows("Accounts Payable", liabilities.currentLiabilities.accountsPayable, "pl-6", "pl-5", "accountsPayableTotal")}
          {renderSubCategoryRows("Credit Card", liabilities.currentLiabilities.creditCard, "pl-6", "pl-5", "creditCardTotal")}
          {renderSubCategoryRows("Other Current Liabilities", liabilities.currentLiabilities.otherCurrentLiabilities, "pl-6", "pl-5", "otherCurrentLiabilitiesTotal")}
          
          <tr className="font-weight-bold small">
            <td className="pl-4" style={{ paddingLeft: '1.75rem' }}>Total for Current Liabilities</td>
            <td className="text-right border-top">{formatCurrency(liabilities.currentLiabilities.total)}</td>
            {comparisons.map((comp, idx) => (
              <td key={idx} className="text-right border-top">{formatCurrency(comp.currentLiabilitiesTotal)}</td>
            ))}
            {renderChangeCells(liabilities.currentLiabilities.total, 'currentLiabilitiesTotal', false)}
          </tr>

          {/* Long Term Liabilities */}
          {liabilities.longTermLiabilities && Object.keys(liabilities.longTermLiabilities.details).length > 0 && (
            <>
              <tr className="font-weight-bold small mt-2"><td className="pl-4" colSpan={totalCols} style={{ paddingLeft: '1.75rem' }}>Long Term Liabilities</td></tr>
              {Object.entries(liabilities.longTermLiabilities.details).map(([name, amount]) => (
                <tr key={name} className="extra-small">
                  {renderAccountNameWithLink(name, "pl-5")}
                  <td className="text-right">{renderCellAmount(name, amount)}</td>
                  {comparisons.map((comp, idx) => (
                    <td key={idx} className="text-right text-muted">{formatCurrency(comp.balances[name] || 0)}</td>
                  ))}
                  {renderChangeCells(amount, name, true)}
                </tr>
              ))}
              <tr className="font-weight-bold small">
                <td className="pl-4" style={{ paddingLeft: '1.75rem' }}>Total for Long Term Liabilities</td>
                <td className="text-right border-top">{formatCurrency(liabilities.longTermLiabilities.total)}</td>
                {comparisons.map((comp, idx) => (
                  <td key={idx} className="text-right border-top">{formatCurrency(comp.longTermLiabilitiesTotal)}</td>
                ))}
                {renderChangeCells(liabilities.longTermLiabilities.total, 'longTermLiabilitiesTotal', false)}
              </tr>
            </>
          )}

          <tr className="font-weight-bold small">
            <td className="pl-3" style={{ paddingLeft: '1rem' }}>Total for Liabilities</td>
            <td className="text-right border-top border-dark">{formatCurrency(liabilities.total)}</td>
            {comparisons.map((comp, idx) => (
              <td key={idx} className="text-right border-top border-dark">{formatCurrency(comp.liabilitiesTotal)}</td>
            ))}
            {renderChangeCells(liabilities.total, 'liabilitiesTotal', false)}
          </tr>

          {/* Equity */}
          <tr className="font-weight-bold small mt-2"><td className="pl-3" colSpan={totalCols} style={{ paddingLeft: '1rem' }}>Equity</td></tr>
          {Object.entries(equity.details).map(([name, amount]) => (
            <tr key={name} className="extra-small">
              {renderAccountNameWithLink(name, "pl-4")}
              <td className="text-right">{renderCellAmount(name, amount)}</td>
              {comparisons.map((comp, idx) => (
                <td key={idx} className="text-right text-muted">{formatCurrency(comp.balances[name] || 0)}</td>
              ))}
              {renderChangeCells(amount, name, true)}
            </tr>
          ))}
          <tr className="font-weight-bold small">
            <td className="pl-3" style={{ paddingLeft: '1rem' }}>Total for Equity</td>
            <td className="text-right border-top">{formatCurrency(equity.total)}</td>
            {comparisons.map((comp, idx) => (
              <td key={idx} className="text-right border-top">{formatCurrency(comp.equityTotalCalculated)}</td>
            ))}
            {renderChangeCells(equity.total, 'equityTotalCalculated', false)}
          </tr>

          {/* Total Liabilities and Equity */}
          <tr className="font-weight-bold border-top border-bottom border-dark bg-dark text-white small">
            <td className="pl-1">TOTAL LIABILITIES AND EQUITY</td>
            <td className="text-right">{formatCurrency(liabilities.total + equity.total)}</td>
            {comparisons.map((comp, idx) => (
              <td key={idx} className="text-right">{formatCurrency(comp.liabilitiesTotal + comp.equityTotal)}</td>
            ))}
            {renderChangeCells(liabilities.total + equity.total, 'liabilities_equity_total_sum_placeholder', false, 'text-white')}
          </tr>
        </tbody>
      </Table>
    );
  };

  const isBalanced = Math.abs(baseData.assets.total - (baseData.liabilities.total + baseData.equity.total)) < 0.01;

  return (
    <Card className="border-0 shadow-sm">
      <Card.Body className="p-4">
        <div className="text-center mb-4">
          <h4 className="mb-1">Balance Sheet</h4>
          <div className="text-muted small">{activeProject.name}</div>
          <div className="text-muted extra-small">As of {moment(endDate).format('MM/DD/YYYY')}</div>
        </div>

        {baseData.groupedData ? (
          <>
            {renderBalanceSheetSection(baseData.groupedData)}
            <div className={`mt-3 text-center p-2 rounded small ${isBalanced ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'}`}>
              {isBalanced 
                ? <><i className="fas fa-check-circle mr-2"></i> Statement Balances</>
                : <><i className="fas fa-exclamation-triangle mr-2"></i> Statement Out of Balance</>}
            </div>
          </>
        ) : (
          <div className="text-center py-4">No structured data available.</div>
        )}
      </Card.Body>
    </Card>
  );
};
